import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Text, Button, InlineStack, Spinner, TextField, ButtonGroup, Icon } from '@shopify/polaris';
import { ChevronLeftMinor, ChevronRightMinor, SearchMinor, ImportMinor, StarFilledMinor } from '@shopify/polaris-icons';
import { authService } from '../services/authService';
import { getServerURL } from '../services/config';

const PAGE_SIZE = 30;

function AIDesignMyFavouritesPage({ setToastMessage, setToastActive }) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [isLastPage, setIsLastPage] = useState(false);
  const [queryInput, setQueryInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const requestIdRef = useRef(0);
  const [hoveredId, setHoveredId] = useState(null);
  const [unfavoritingIds, setUnfavoritingIds] = useState(new Set());

  const fetchPage = useCallback(async (pageNumber, query) => {
    try {
      setIsLoading(true);
      const baseUrl = getServerURL();
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      params.set('page', String(pageNumber));
      if (query && query.trim()) params.set('q', query.trim());
      const currentRequestId = ++requestIdRef.current;
      const resp = await fetch(`${baseUrl}/design/favorites?${params.toString()}`, {
        headers: authService.getAuthHeaders(),
      });
      if (currentRequestId !== requestIdRef.current) return;
      if (!resp.ok) {
        setItems([]);
        setIsLastPage(true);
        setTotal(0);
        setPages(pageNumber);
        return;
      }
      const json = await resp.json();
      if (currentRequestId !== requestIdRef.current) return;
      const data = Array.isArray(json?.data) ? json.data : Array.isArray(json?.items) ? json.items : Array.isArray(json) ? json : [];
      const respPage = Number(json?.page) || pageNumber;
      const respTotal = Number(json?.total) || (Array.isArray(json?.data) && typeof json?.total === 'number' ? Number(json.total) : (respPage - 1) * PAGE_SIZE + data.length);
      const respPages = Number(json?.pages) || (respTotal ? Math.max(1, Math.ceil(respTotal / PAGE_SIZE)) : (data.length < PAGE_SIZE ? respPage : respPage + 1));

      setItems(data);
      setIsLastPage(respPage >= respPages || data.length < PAGE_SIZE);
      setTotal(isNaN(respTotal) ? 0 : respTotal);
      setPages(isNaN(respPages) ? respPage : respPages);
    } catch (_err) {
      setItems([]);
      setIsLastPage(true);
      setTotal(0);
      setPages(page);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch whenever page or searchQuery changes
  useEffect(() => {
    fetchPage(page, searchQuery);
  }, [page, searchQuery, fetchPage]);

  const canGoPrev = page > 1 && !isLoading;
  const canGoNext = !isLoading && page < pages;

  const filteredItems = items;

  // Build a segmented pagination window like ScenesPage (up to 5 pages)
  const getVisiblePages = () => {
    const totalPages = pages || page;
    let end = Math.min(totalPages, page + 4);
    let start = Math.max(1, end - 4);
    if (page < start) start = page - 2;
    if (page > end) end = page + 2;
    start = Math.max(1, start);
    end = Math.min(totalPages, end);
    const pagesArr = [];
    for (let p = start; p <= end; p++) pagesArr.push(p);
    return pagesArr;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Box padding="400" className="Polaris-ShadowBevel">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <Text as="h2" variant="headingMd">My Favourites</Text>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <div style={{ minWidth: 260 }}>
              <TextField
                label="Search by prompt, story or scene..."
                value={queryInput}
                onChange={setQueryInput}
                placeholder="Search by prompt, story or scene..."
                autoComplete="off"
                prefix={<Icon source={SearchMinor} tone="base" />}
                labelHidden
              />
            </div>
            <Button
              variant="primary"
              onClick={() => {
                const q = queryInput.trim();
                if (q === searchQuery && page === 1) {
                  fetchPage(1, q);
                  return;
                }
                setPage(1);
                setSearchQuery(q);
              }}
            >
              Search
            </Button>
          </div>
        </div>

        <div style={{ position: 'relative' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
            {filteredItems.map((item) => {
              const imageUrl = item?.outputImageUrl || item?.inputImageUrl || '';
              const key = item?._id || imageUrl;
              const isFavorited = true; // by definition, items here are favorites
              const isUnfaving = unfavoritingIds.has(item?._id);
              return (
                <div
                  key={key}
                  className="Polaris-ShadowBevel"
                  style={{
                    padding: 0,
                    width: '100%',
                    aspectRatio: '1 / 1',
                    overflow: 'hidden',
                    background: '#f5f6f7',
                    display: 'block',
                    position: 'relative',
                  }}
                  onMouseEnter={() => setHoveredId(key)}
                  onMouseLeave={() => setHoveredId((prev) => (prev === key ? null : prev))}
                >
                  {/* Hover actions */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      display: 'flex',
                      gap: 8,
                      opacity: hoveredId === key ? 1 : 0,
                      transition: 'opacity 120ms ease-in-out',
                      zIndex: 2,
                    }}
                  >
                    <Button
                      size="slim"
                      icon={ImportMinor}
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const resp = await fetch(imageUrl, { mode: 'cors' });
                          const blob = await resp.blob();
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          const filename = (imageUrl.split('/').pop() || 'image').split('?')[0] || 'image';
                          a.download = filename;
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          URL.revokeObjectURL(url);
                        } catch (_ignore) {}
                      }}
                      accessibilityLabel="Download image"
                    />
                    <Button
                      size="slim"
                      icon={StarFilledMinor}
                      variant={isFavorited ? 'primary' : 'tertiary'}
                      tone={isFavorited ? 'warning' : undefined}
                      disabled={isUnfaving || !item?._id}
                      onClick={async (e) => {
                        e.stopPropagation();
                        const designId = item?._id;
                        if (!designId) return;
                        if (unfavoritingIds.has(designId)) return;
                        const nextUnfav = new Set(unfavoritingIds);
                        nextUnfav.add(designId);
                        setUnfavoritingIds(nextUnfav);
                        try {
                          const baseUrl = getServerURL();
                          const resp = await fetch(`${baseUrl}/design/favorites/${designId}`, {
                            method: 'DELETE',
                            headers: authService.getAuthHeaders(),
                          });
                          if (resp.ok) {
                            // Optimistically remove from current list
                            setItems((prev) => prev.filter((x) => x?._id !== designId));
                            setTotal((t) => Math.max(0, (t || 0) - 1));
                            if (typeof setToastMessage === 'function' && typeof setToastActive === 'function') {
                              setToastMessage('Removed from favourites');
                              setToastActive(true);
                              setTimeout(() => setToastActive(false), 2000);
                            }
                            // If the page becomes empty and there are previous pages, go back one page to refetch
                            setTimeout(() => {
                              if (items.length <= 1 && page > 1) {
                                setPage((p) => Math.max(1, p - 1));
                              } else {
                                // refresh current page to ensure server/client stay in sync
                                fetchPage(page, searchQuery);
                              }
                            }, 0);
                          }
                        } catch (_err) {
                          // ignore for now
                        } finally {
                          const next = new Set(unfavoritingIds);
                          next.delete(designId);
                          setUnfavoritingIds(next);
                        }
                      }}
                      accessibilityLabel="Unfavourite image"
                    />
                  </div>
                  {imageUrl ? (
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        backgroundImage: `url(${imageUrl})`,
                        backgroundSize: 'contain',
                        backgroundPosition: 'center center',
                        backgroundRepeat: 'no-repeat',
                      }}
                    />
                  ) : null}
                </div>
              );
            })}
          </div>

          {isLoading && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Spinner size="large" />
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 16 }}>
          {(pages > 1) && (
            <>
              <ButtonGroup variant="segmented">
                <Button icon={ChevronLeftMinor} disabled={!canGoPrev} onClick={() => setPage((p) => Math.max(1, p - 1))} />
                {getVisiblePages().map((p) => (
                  <Button key={p} pressed={p === page} onClick={() => setPage(p)} variant={p === page ? 'secondary' : 'secondary'}>
                    {p}
                  </Button>
                ))}
                <Button icon={ChevronRightMinor} disabled={!canGoNext} onClick={() => setPage((p) => p + 1)} />
              </ButtonGroup>
              <div style={{ marginTop: 8, marginBottom: 16, color: '#6d7175', fontSize: 13 }}>
                {Math.max(0, (page - 1) * PAGE_SIZE + (items.length > 0 ? 1 : 0))}
                –
                {Math.min(page * PAGE_SIZE, total)}
                {' of '}
                {total}
                {' (Page '}{page}{' of '}{pages}{')'}
              </div>
            </>
          )}
        </div>
      </Box>
    </div>
  );
}

export default AIDesignMyFavouritesPage;


